import { apiClient } from './apiClient';

// Limits mirror the API (activityFiles.service.js) so people are told at once
// rather than after an upload.
export const MAX_REPLY_FILES = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 10 * 1024 * 1024;

export const formatFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Adds newly chosen files to the current list, or explains why it can't.
// Returns { files, error } - on error the list is unchanged.
export const addReplyFiles = (current, incoming) => {
  const next = [...current, ...Array.from(incoming || [])];
  if (next.length > MAX_REPLY_FILES) {
    return { files: current, error: `You can attach up to ${MAX_REPLY_FILES} files to a reply.` };
  }
  const tooBig = next.find((f) => f.size > MAX_FILE_BYTES);
  if (tooBig) return { files: current, error: `${tooBig.name} is larger than 10MB.` };
  if (next.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL_BYTES) {
    return { files: current, error: 'Attachments on one reply are limited to 10MB in total.' };
  }
  return { files: next, error: '' };
};

// Files pasted into the reply box (e.g. a screenshot from the clipboard).
// Clipboard images arrive unnamed ("image.png"), so give them a unique name.
export const filesFromPaste = (event) => Array.from(event.clipboardData?.files || []).map((file, i) => (
  file.name && file.name !== 'image.png'
    ? file
    : new File([file], `pasted-${Date.now()}-${i + 1}.${(file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: file.type })
));

// Uploads to the ticket's Files, returning the attachment ids to send with the
// reply. Uses the same endpoint (and ownership checks) as the Files panel.
export const uploadTicketFiles = async (ticketId, files) => {
  const ids = [];
  for (const file of files) {
    const query = new URLSearchParams({
      entity_type: 'ticket',
      entity_id: String(ticketId),
      filename: file.name,
      content_type: file.type || 'application/octet-stream'
    });
    const row = await apiClient.upload(`/api/attachments?${query.toString()}`, file);
    ids.push(row.id);
  }
  return ids;
};
